import React, { useState, useEffect } from "react";
import Sidebar from "../../components/Admin/Sidebar";
import Header from "../../components/Admin/Header";
import api from "../../api/axois";
import jsPDF from "jspdf";
import styles from "./Payments.module.css";

const monthMap = {
  January: "Jan",
  February: "Feb",
  March: "Mar",
  April: "Apr",
  May: "May",
  June: "Jun",
  July: "Jul",
  August: "Aug",
  September: "Sep",
  October: "Oct",
  November: "Nov",
  December: "Dec",
};

const Payments = () => {
  const [allPayments, setAllPayments] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All Payments");
  const [month, setMonth] = useState("All Months");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 5,
    total: 0,
  });
  const itemsPerPage = 5;
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    fetchPayments(1);
  }, []);

  const fetchPayments = (page) => {
    api.get(`/payments?page=${page}&per_page=${itemsPerPage}`).then((res) => {
      setAllPayments(res.data.payments || []);
      setPagination(
        res.data.pagination || {
          current_page: page,
          last_page: 1,
          per_page: itemsPerPage,
          total: 0,
        }
      );
      setCurrentPage(page);
    });
  };

  const extractShortMonth = (dateStr) => {
    const match = dateStr.match(
      /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\b/
    );
    return match ? match[0] : "";
  };

  const filteredPayments = allPayments.filter((payment) => {
    const nameMatch = (payment.name || "").toLowerCase().includes(search.toLowerCase());
    const monthMatch =
      month === "All Months" ||
      extractShortMonth(payment.date) === monthMap[month] ||
      extractShortMonth(payment.date) === month;
    const statusMatch =
      filter === "All Payments" ||
      (filter === "Successful" && payment.status === "success") ||
      (filter === "Failed" && payment.status === "failed");
    return nameMatch && monthMatch && statusMatch;
  });

  const totalPages = pagination.last_page;


  const handleDownload = (payment) => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();

    // ── Palette ──────────────────────────────────────────────────────────────
    const NAVY   = [35,  61, 129];   // #233d81
    const GOLD   = [212, 175,  55];  // #d4af37
    const WHITE  = [255, 255, 255];
    const LIGHT  = [247, 248, 250];  // #f7f8fa
    const MUTED  = [113, 128, 150];  // #718096
    const DARK   = [45,  55,  72];   // #2d3748
    const GREEN  = [39, 174,  96];
    const RED    = [192,  57,  43];

    // ── Header band ──────────────────────────────────────────────────────────
    doc.setFillColor(...NAVY);
    doc.rect(0, 0, W, 90, "F");

    // Gold accent bar
    doc.setFillColor(...GOLD);
    doc.rect(0, 90, W, 4, "F");


    // Company name & tagline (right of logo)
    doc.setTextColor(...WHITE);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text("AURELIA", 110, 48);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...GOLD);
    doc.text("APARTMENTS", 110, 64);

    // "PAYMENT RECEIPT" label — right-aligned
    doc.setTextColor(...WHITE);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("PAYMENT RECEIPT", W - 30, 52, { align: "right" });

    // ── Receipt meta row ──────────────────────────────────────────────────────
    doc.setFillColor(...LIGHT);
    doc.rect(0, 94, W, 44, "F");

    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("RECEIPT NO.", 30, 113);
    doc.text("DATE ISSUED", 200, 113);
    doc.text("STATUS", W - 140, 113);

    doc.setTextColor(...DARK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    const receiptNo = `AUR-${String(payment.id).padStart(6, "0")}`;
    doc.text(receiptNo, 30, 128);
    doc.text(payment.date || new Date().toLocaleDateString(), 200, 128);

    const statusText  = (payment.status || "unknown").toUpperCase();
    const statusColor = payment.status === "success" ? GREEN : RED;
    doc.setTextColor(...statusColor);
    doc.text(statusText, W - 140, 128);

    // ── Divider ───────────────────────────────────────────────────────────────
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(0.5);
    doc.line(30, 152, W - 30, 152);

    // ── "Billed To" section ───────────────────────────────────────────────────
    let y = 175;
    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("BILLED TO", 30, y);

    y += 16;
    doc.setTextColor(...DARK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(payment.name || "Guest", 30, y);

    y += 16;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    if (payment.email) { doc.text(payment.email, 30, y); y += 14; }
    if (payment.phone) { doc.text(payment.phone, 30, y); y += 14; }

    // Booking type badge
    y += 4;
    const badgeLabel = payment.booking_type || "Guest";
    const badgeColor = badgeLabel === "Registered User" ? NAVY : [102, 126, 234];
    doc.setFillColor(...badgeColor);
    doc.roundedRect(30, y, doc.getTextWidth(badgeLabel) + 16, 18, 3, 3, "F");
    doc.setTextColor(...WHITE);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(badgeLabel, 38, y + 12);

    // ── Payment summary box ───────────────────────────────────────────────────
    y += 40;
    doc.setFillColor(...LIGHT);
    doc.roundedRect(30, y, W - 60, 100, 6, 6, "F");
    doc.setDrawColor(220, 225, 235);
    doc.setLineWidth(0.5);
    doc.roundedRect(30, y, W - 60, 100, 6, 6, "S");

    // Row helper
    const row = (label, value, rowY, bold = false) => {
      doc.setTextColor(...MUTED);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(label, 50, rowY);
      doc.setTextColor(...DARK);
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.text(value || "—", W - 50, rowY, { align: "right" });
    };

    row("Receipt No.", receiptNo,         y + 24);
    row("Date",        payment.date,       y + 44);
    row("Booking Type",payment.booking_type || "Guest", y + 64);

    // Divider inside box
    doc.setDrawColor(220, 225, 235);
    doc.line(50, y + 76, W - 50, y + 76);

    // Amount row — larger and gold
    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("Total Amount", 50, y + 92);
    doc.setTextColor(...GOLD);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    // Replace ₦ with NGN because jsPDF's built-in Helvetica font does not support
    // Unicode characters outside Latin-1 (₦ is U+20A6)
    const amountDisplay = payment.amount
      ? payment.amount.replace(/₦/g, "NGN ")
      : "\u2014";
    doc.text(amountDisplay, W - 50, y + 94, { align: "right" });

    // ── Footer ────────────────────────────────────────────────────────────────
    doc.setFillColor(...NAVY);
    doc.rect(0, H - 70, W, 70, "F");

    doc.setFillColor(...GOLD);
    doc.rect(0, H - 74, W, 4, "F");

    doc.setTextColor(...WHITE);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Thank you for choosing Aurelia!", W / 2, H - 48, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...GOLD);
    doc.text("aurelia-apartments.com  •  support@aurelia-apartments.com", W / 2, H - 30, { align: "center" });

    doc.save(`aurelia-receipt-${receiptNo}.pdf`);
  };

  return (
    <div className={styles.container}>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className={styles.content}>
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <div className={styles.paymentsPage}>
          <h2 className={styles.pageTitle}>Recent Payments</h2>

          <div className={styles.filters}>
            <input
              type="text"
              placeholder="Search User Name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option>All Payments</option>
              <option>Successful</option>
              <option>Failed</option>
            </select>

            <select value={month} onChange={(e) => setMonth(e.target.value)}>
              <option>All Months</option>
              <option>January</option>
              <option>February</option>
              <option>March</option>
              <option>April</option>
              <option>May</option>
              <option>June</option>
              <option>July</option>
              <option>August</option>
              <option>September</option>
              <option>October</option>
              <option>November</option>
              <option>December</option>
            </select>
          </div>

          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Type</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Receipt</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.length > 0 ? (
                filteredPayments.map((payment, index) => (
                  <tr key={index}>
                    <td>{payment.name || "N/A"}</td>
                    <td>{payment.email || payment.guest_email || "N/A"}</td>
                    <td>{payment.phone || payment.guest_phone || "N/A"}</td>
                    <td>
                      <span
                        className={`${styles.bookingType} ${
                          styles[
                            payment.booking_type === "Guest" ? "guest" : "user"
                          ]
                        }`}
                      >
                        {payment.booking_type}
                      </span>
                    </td>
                    <td>{payment.date}</td>
                    <td className={styles.amount}>{payment.amount}</td>
                    <td>
                      <span
                        className={`${styles.status} ${styles[
                          (payment.status || "unknown").toLowerCase()
                        ]}`}
                      >
                        {payment.status || "—"}
                      </span>
                    </td>
                    <td>
                      <button
                        className={styles.downloadBtn}
                        onClick={() => handleDownload(payment)}
                      >
                        Download ⬇
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className={styles.noResults}>
                    No results found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className={styles.pagination}>
            <button
              className={styles.pageButton}
              onClick={() => fetchPayments(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1}
            >
              ‹ Previous
            </button>
            {Array.from({ length: totalPages }, (_, n) => (
              <button
                key={n}
                onClick={() => fetchPayments(n + 1)}
                className={`${styles.pageButton} ${
                  currentPage === n + 1 ? styles.active : ""
                }`}
              >
                {n + 1}
              </button>
            ))}
            <button
              className={styles.pageButton}
              onClick={() => fetchPayments(Math.min(currentPage + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next ›
            </button>
          </div>
        </div>
      </div>
      {sidebarOpen && (
        <div className={styles.overlay} onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
};

export default Payments;
